import { useEffect } from "react";
import type { ActorRefFrom, SnapshotFrom } from "xstate";
import { useSetAtom } from "jotai";
import { X } from "lucide-react";
import { Modal } from "@design-lab/shared/ui/modal";
import type { artworkMachine } from "@design-lab/entities/artwork/model/artwork.machine";
import { mobileDrawerExpandedAtom } from "@design-lab/widgets/artboard/model/state";
import { ArtworkFeedbackFormModalContent } from "./ArtworkFeedbackFormModalContent";
import { FeedbackThumbActions } from "./FeedbackThumbActions";
import { ArtworkFeedbackThankYou } from "./ArtworkFeedbackThankYou";
import type { FeedbackStates } from "./ArtworkFeedback";

type ArtworkFeedbackProps = {
  artworkState: SnapshotFrom<typeof artworkMachine>;
  sendArtwork: ActorRefFrom<typeof artworkMachine>["send"];
  onPositiveClick: () => void;
  onNegativeClick: () => void;
  onClose: () => void;
  feedbackStates: FeedbackStates;
};

export function ArtworkFeedbackMobile({
  artworkState,
  sendArtwork,
  onPositiveClick,
  onNegativeClick,
  onClose,
  feedbackStates
}: ArtworkFeedbackProps) {
  const { isIdleState, isFeedbackFormState, isThankYouState, isThankYouNegativeState } =
    feedbackStates;

  const setMobileDrawerExpanded = useSetAtom(mobileDrawerExpandedAtom);

  // Collapse mobile drawer only when initial feedback popup appears after successful upload
  useEffect(() => {
    if (isIdleState) {
      setMobileDrawerExpanded(false);
    }
  }, [isIdleState, setMobileDrawerExpanded]);

  return (
    <>
      {(isIdleState || isThankYouState || isThankYouNegativeState) && (
        <div className="animate-in slide-in-from-bottom absolute bottom-0 left-0 right-0 z-50 duration-300">
          <div className="rounded-t-2xl bg-white shadow-2xl">
            {isIdleState && (
              <>
                <div className="relative flex items-center justify-between px-4 pb-2 pt-4">
                  <h3 className="text-sm font-medium text-gray-900">Happy with your image?</h3>
                  <button
                    onClick={onClose}
                    className="absolute right-3 top-3 mb-1 rounded-full p-1 transition-colors hover:bg-gray-100"
                    aria-label="Close feedback"
                    data-testid="artwork-feedback-close"
                  >
                    <X className="size-6 text-gray-500" />
                  </button>
                </div>
                <div className="px-4 pb-4">
                  <FeedbackThumbActions
                    onPositiveClick={onPositiveClick}
                    onNegativeClick={onNegativeClick}
                    showLabel={false}
                  />
                </div>
              </>
            )}

            {(isThankYouState || isThankYouNegativeState) && (
              <div className="relative">
                <button
                  onClick={onClose}
                  className="absolute right-3 top-3 z-10 rounded-full p-1 transition-colors hover:bg-gray-100"
                  aria-label="Close feedback"
                  data-testid="artwork-feedback-thankyou-close"
                >
                  <X className="size-6 text-gray-500" />
                </button>
                <ArtworkFeedbackThankYou className="min-h-fit p-5" />
              </div>
            )}
          </div>
        </div>
      )}

      {isFeedbackFormState && (
        <Modal
          isOpen={true}
          className="translate-none bottom-0 left-0 top-auto flex h-auto max-h-[90vh] w-full flex-col rounded-b-none"
          closeOnBackdropClick={false}
          closeOnEsc={false}
        >
          <ArtworkFeedbackFormModalContent artworkState={artworkState} sendArtwork={sendArtwork} />
        </Modal>
      )}
    </>
  );
}
